import { api } from "@awtoban/shared";
import { Button, Input, Label, toast } from "@awtoban/ui";
import { useQuery } from "@tanstack/react-query";
import { Link, useNavigate } from "@tanstack/react-router";
import { LogOut } from "lucide-react";
import * as React from "react";
import { useTranslation } from "react-i18next";
import { LocaleSwitcher } from "../../i18n/LocaleSwitcher";
import { useAuth } from "../auth/AuthProvider";

interface HQOperator {
  id: number;
  username: string;
  display_name: string;
  station_code: string | null;
  station_name: string | null;
  has_pin: boolean;
}

const PIN_RE = /^\d{4,6}$/;

export function OperatorsPage() {
  const { t } = useTranslation();
  const auth = useAuth();
  const navigate = useNavigate();
  const [editing, setEditing] = React.useState<number | null>(null);

  const operators = useQuery({
    queryKey: ["hq-operators"],
    queryFn: async () => {
      const { data, error } = await api.GET("/api/v1/hq/operators", {});
      if (error || !data) throw new Error("operators_failed");
      return data as unknown as HQOperator[];
    },
  });

  return (
    <div className="min-h-screen bg-paper-soft">
      <header className="flex flex-wrap items-center gap-x-6 gap-y-2 border-line-strong border-b bg-paper px-4 py-3 sm:px-6">
        <div className="flex items-center gap-6">
          <Link to="/" className="text-ink-muted text-sm hover:text-ink">
            {t("nav.dashboard")}
          </Link>
          <span className="font-medium text-sm">{t("nav.operators")}</span>
        </div>
        <div className="ml-auto flex flex-wrap items-center gap-4 text-sm sm:gap-6">
          <div className="font-medium">{auth.user?.username}</div>
          <LocaleSwitcher />
          <Button
            variant="ghost"
            size="sm"
            onClick={() => {
              auth.logout();
              void navigate({ to: "/login" });
            }}
          >
            <LogOut className="h-4 w-4" />
            {t("auth.log_out")}
          </Button>
        </div>
      </header>

      <main className="mx-auto max-w-[900px] px-4 py-4 sm:px-6 sm:py-6">
        <section className="rounded-md border border-line-strong bg-paper">
          <h2 className="border-line border-b px-4 py-2 font-medium text-ink-muted text-xs uppercase tracking-wider">
            {t("operators.title")}
          </h2>
          {operators.isLoading ? (
            <div className="px-4 py-6 text-ink-muted text-sm">{t("operators.loading")}</div>
          ) : (operators.data ?? []).length === 0 ? (
            <div className="px-4 py-6 text-ink-muted text-sm">{t("operators.empty")}</div>
          ) : (
            <ul className="divide-y divide-line">
              {(operators.data ?? []).map((op) => (
                <OperatorRow
                  key={op.id}
                  op={op}
                  editing={editing === op.id}
                  onToggle={() => setEditing(editing === op.id ? null : op.id)}
                  onClose={() => setEditing(null)}
                  onSaved={() => {
                    setEditing(null);
                    void operators.refetch();
                  }}
                />
              ))}
            </ul>
          )}
        </section>
      </main>
    </div>
  );
}

function OperatorRow({
  op,
  editing,
  onToggle,
  onClose,
  onSaved,
}: {
  op: HQOperator;
  editing: boolean;
  onToggle: () => void;
  onClose: () => void;
  onSaved: () => void;
}) {
  const { t } = useTranslation();
  return (
    <li className="px-4 py-3">
      <div className="flex items-center gap-3">
        <div className="flex-1">
          <div className="font-medium">{op.display_name}</div>
          <div className="text-[12px] text-ink-muted">
            {op.station_code ?? "—"}
            {" · "}
            <span className={op.has_pin ? "text-success-fg" : "text-ink-subtle"}>
              {op.has_pin ? t("operators.has_pin") : t("operators.no_pin")}
            </span>
          </div>
        </div>
        <Button variant="secondary" size="sm" onClick={onToggle}>
          {op.has_pin ? t("operators.reset_pin") : t("operators.set_pin")}
        </Button>
      </div>
      {editing ? (
        <SetPinRow operatorId={op.id} name={op.display_name} onClose={onClose} onSaved={onSaved} />
      ) : null}
    </li>
  );
}

function SetPinRow({
  operatorId,
  name,
  onClose,
  onSaved,
}: {
  operatorId: number;
  name: string;
  onClose: () => void;
  onSaved: () => void;
}) {
  const { t } = useTranslation();
  const [pin, setPin] = React.useState("");
  const [error, setError] = React.useState<string | null>(null);
  const [saving, setSaving] = React.useState(false);

  const save = async () => {
    if (!PIN_RE.test(pin) || saving) {
      setError(t("operators.pin_format"));
      return;
    }
    setSaving(true);
    const { error: apiError } = await api.POST("/api/v1/shift/set-pin", {
      body: { operator_id: operatorId, pin } as never,
    });
    setSaving(false);
    if (apiError) {
      setError(t("operators.error"));
      return;
    }
    toast.success(t("operators.saved", { name }));
    onSaved();
  };

  return (
    <div className="mt-3 flex items-end gap-2">
      <div className="flex-1">
        <Label htmlFor={`pin-${operatorId}`}>{t("operators.new_pin")}</Label>
        <Input
          id={`pin-${operatorId}`}
          inputMode="numeric"
          autoFocus
          maxLength={6}
          value={pin}
          onChange={(e) => {
            setError(null);
            setPin(e.target.value.replace(/\D/g, ""));
          }}
          className="tabular tracking-[0.3em]"
        />
        {error ? <div className="mt-1 text-[13px] text-danger">{error}</div> : null}
      </div>
      <Button variant="secondary" size="sm" onClick={onClose}>
        {t("operators.cancel")}
      </Button>
      <Button size="sm" disabled={saving || pin.length < 4} onClick={() => void save()}>
        {saving ? t("operators.saving") : t("operators.save")}
      </Button>
    </div>
  );
}
